import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  OperatorFunction,
  pipe,
  Subject,
} from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { SessionService } from './session.service';
import { SocketConnectionService } from './socket-connection.service';
import { Message } from '../../chat/interaction/models/message.model';
import { LocalStorage } from '../enums/local-storage.enum';
import { InteractionEventType } from 'app/chat/interaction/models/interaction-event-type';
import { v4 as uuid } from 'uuid';
import { ConfigService } from './config.service';
import { MessagesUtil } from './utils/messagesUtil';
import { HttpApiService } from './http-api/http-api.service';
import { UnreadMessageService } from './unread-message.service';

@Injectable()
export class MessageService {
  messages$ = new BehaviorSubject<Message[]>(
    this.convertLocalStorageToMessage() ?? []
  );
  noMoreHistory: boolean = false;
  tempDate = null;

  destroy$ = new Subject<void>();
  GET_STARTED = 'get_started_web_chat_bot_logate';

  TIME_FOR_DELIVERY_STATUS = 2 /**s */ * 1000 /**ms */;

  FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  areMessagesVisible: boolean;
  isTabSelected: boolean = true;
  messageToBeSeenId = null;
  constructor(
    private socketConnectionService: SocketConnectionService,
    private sessionService: SessionService,
    private httpApiService: HttpApiService,
    private messagesUtil: MessagesUtil,
    private unreadMessages: UnreadMessageService,
    private configService: ConfigService
  ) {
    this.handleReload();
    document.addEventListener(
      'visibilitychange',
      this.onVisibilityChange.bind(this)
    );
    this.addDateLine();
  }

  addDateLine() {
    this.messages$.subscribe((messages) => {
      messages.forEach((message) => {
        if (message.date === undefined) return;
        if (this.getDateFromDateTime(message.date) !== this.tempDate) {
          message.firstOfNewDay = this.getDateFromDateTime(message.date);
          this.tempDate = this.getDateFromDateTime(message.date);
        } else message.firstOfNewDay = null;
      });
    });
  }

  getDateFromDateTime(dateTime: any): string {
    if (dateTime instanceof Date) {
      return dateTime.toISOString().split('T')[0];
    } else {
      const indexOfT = dateTime.indexOf('T');
      return indexOfT !== -1 ? dateTime.substring(0, indexOfT) : dateTime;
    }
  }

  onVisibilityChange(): void {
    this.isTabSelected = document.visibilityState === 'visible';
    if (
      this.areMessagesVisible &&
      this.isTabSelected &&
      this.messageToBeSeenId !== null
    ) {
      this.sendSeenStatus(this.messageToBeSeenId);
      this.messageToBeSeenId = null;
    }
  }

  handleReload() {
    if (
      this.sessionService.getChatSessionId() &&
      this.messages$.value.length === 0
    ) {
      this.loadInitialHistory();
    }
  }

  watchForIncomingMessages(resetUnreadMessages = true): Observable<any> {
    this.noMoreHistory = false;
    this.destroy$ = new Subject<void>();
    if (resetUnreadMessages) {
      this.unreadMessages.unreadMessages$.next(0);
    }
    return this.socketConnectionService
      .watchMessages()
      .pipe(takeUntil(this.destroy$))
      .pipe(this.handleIncomingMessages());
  }

  handleIncomingMessages(): OperatorFunction<any, any> {
    return pipe(
      map((message) => JSON.parse(message.body)),
      map((message) => this.messagesUtil.mapToMessagesAndQuickReplies(message)),
      tap((message) => this.updateMultipleMessages(message)),
      tap((messages) => {
        messages.forEach((message) => {
          if (!message.isQuickReply) this.sendDeliveredStatus(message.id);
        });
      }),
      tap(() => this.clearQuickRepliesBeforeLastMessage())
    );
  }

  sendDeliveredStatus(messageId: number) {
    if (!messageId) {
      console.error('Message id is not defined');
      return;
    }
    const body = {
      id: messageId,
      chatSessionId: this.sessionService.getChatSessionId(),
      eventType: 'MESSAGE_DELIVERED',
    };
    this.socketConnectionService.publishDelivered(body);

    if (this.areMessagesVisible && this.isTabSelected) {
      this.sendSeenStatus(messageId);
    } else {
      this.messageToBeSeenId = messageId;
      if (!this.configService.getConfig().token)
        this.unreadMessages.incrementUnreadMessages();
    }
  }

  sendSeenStatus(messageId: number) {
    if (!messageId) {
      console.error('Message id is not defined');
      return;
    }
    const body = {
      id: messageId,
      chatSessionId: this.sessionService.getChatSessionId(),
      eventType: 'MESSAGE_SEEN',
    };
    this.socketConnectionService.publishDelivered(body);
    this.unreadMessages.unreadMessages$.next(0);
  }

  clearQuickRepliesBeforeLastMessage(): void {
    const lastMessageIndex = this.messagesUtil.findLastMessageIndex(
      this.messages$.value
    );
    if (lastMessageIndex === -1) return;

    const lastMessagePosition = this.messagesUtil.getLastMessagePosition(
      this.messages$.value,
      lastMessageIndex
    );

    const filteredMessages =
      this.messagesUtil.filterOutQuickRepliesBeforeLastMessage(
        this.messages$.value,
        lastMessagePosition
      );

    this.messages$.next(filteredMessages);
  }

  sendGetStartedMessage() {
    const message = new Message({
      externalId: uuid(),
      content: this.GET_STARTED,
      direction: 'IN',
      date: new Date(),
      quickReplyData: {
        actionBodyId: 1,
        actionBodyCode: this.GET_STARTED,
      },
    });
    this.sendMessage(message, this.sessionService.getChatSessionId());
  }

  convertLocalStorageToMessage() {
    if (!localStorage.getItem(LocalStorage.LOGATE_CHAT_MESSAGES)) {
      return;
    }

    return JSON.parse(
      localStorage.getItem(LocalStorage.LOGATE_CHAT_MESSAGES)
    ).map((message) => {
      return new Message({
        deliveryStatus: message.deliveryStatus,
        externalId: message?.externalId,
        id: message?.id,
        content: message.content,
        direction: message.direction,
        date: new Date(message.date),
        isQuickReply: message.isQuickReply,
        predefined: message.predefined,
        bgColor: message.bgColor,
        imageLink: message.imageLink,
        textColor: message.textColor,
        quickReplyData: message.quickReplyData
          ? {
              externalId: message.quickReplyData.externalId,
              actionBodyCode: message.quickReplyData.actionBodyCode,
              actionBodyId: message.quickReplyData.actionBodyId,
              bgColor: message.quickReplyData.bgColor,
              imageLink: message.quickReplyData.imageLink,
              message: message.quickReplyData.text,
              textColor: message.quickReplyData.textColor,
              previous: message.quickReplyData.previous,
            }
          : undefined,
        file: message.file,
        fileData: message.fileData,
      });
    });
  }

  loadInitialHistory() {
    this.noMoreHistory = false;
    this.httpApiService
      .getMoreMessageHistory(this.sessionService.getChatSessionId(), 20)
      .subscribe((messageHistory) => {
        this.messages$.next(
          this.messagesUtil.mapToMessagesAndQuickReplies(messageHistory)
        );
      });
  }

  loadMoreHistory(): Promise<void> {
    return new Promise((resolve) => {
      if (this.noMoreHistory) {
        return;
      }
      this.httpApiService
        .getMoreMessageHistory(
          this.sessionService.getChatSessionId(),
          20,
          this.messages$.value[0].id
        )
        .subscribe((messageHistory) => {
          if (messageHistory.messages.length === 0) {
            this.noMoreHistory = true;
          }

          this.messages$.next([
            ...this.messagesUtil.mapMessageDTOToMessages(messageHistory),
            ...this.messages$.getValue(),
          ]);
          resolve();
        });
    });
  }

  resendMessage(message: Message) {
    // first step is to delete message from messages$ and localStorage
    this.deleteMessage(message);
    // send again as regular message
    this.sendMessage(message, this.sessionService.getChatSessionId());
  }

  deleteMessage(message: Message) {
    const messageIndex = this.messagesUtil.getMessageIndex(
      message.externalId,
      this.messages$.getValue()
    );
    if (messageIndex === -1) {
      return;
    }

    this.messages$.next(this.messages$.getValue().splice(messageIndex, 1));

    this.messagesUtil.updateLocalStorageMessages(this.messages$.getValue());
  }

  sendMessage(message: Message, chatSessionId: string): void {
    const request = this.messagesUtil.prepareSendMessageRequest(
      message,
      chatSessionId
    );

    const headers = {
      externalId: request.externalId,
      chatSessionId: chatSessionId,
    };
    if (chatSessionId) {
      this.socketConnectionService.publishMessage(request, headers);
      this.updateMessages(message);
    }
  }

  checkDeliveryStatus = (message: Message) => {
    // if there is no still delivery status to message, set it to MESSAGE_UNDELIVERABLE
    const messageInStore = this.messages$
      .getValue()
      .find((m) => m.externalId === message.externalId);
    // if there is no message in store, return
    if (!messageInStore) {
      return;
    }
    // if there is already delivery status, return
    if (messageInStore.deliveryStatus) {
      return;
    }

    if (messageInStore.id) {
      return;
    }

    this.setDeliveryStatus(
      message.externalId,
      InteractionEventType.MESSAGE_UNDELIVERABLE
    );
  };

  updateMultipleMessages(messages: Message[]) {
    messages.forEach((m) => {
      this.updateMessages(m);
    });
  }

  createErrorMessage(error: string) {
    const message = new Message({
      externalId: uuid(),
      direction: 'OUT',
      content: `Error occurred during sending files: ${error}`,
      date: new Date(),
      isQuickReply: false,
    });
    this.updateMessages(message);
  }

  updateMessages(message: Message): void {
    if (message.content === this.GET_STARTED) {
      return;
    }
    // check if there is already message with same externalID
    if (
      !this.messagesUtil.existingExternalId(
        this.messages$.getValue(),
        message.externalId
      )
    ) {
      this.messages$.next([...this.messages$.getValue(), message]);

      this.messagesUtil.updateLocalStorageMessages(this.messages$.getValue());
    }
    if (message.direction === 'IN') {
      setTimeout(
        this.checkDeliveryStatus,
        this.TIME_FOR_DELIVERY_STATUS,
        message
      );
    }
  }

  getNumberOfMessages(): Observable<number> {
    return this.messages$.pipe(map((messages) => messages.length));
  }

  public setDeliveryStatus(
    externalId: string,
    deliveryStatus: InteractionEventType
  ) {
    // find external id in messages array
    const messages = this.messages$.getValue();
    const messageIndex = this.messagesUtil.getMessageIndex(
      externalId,
      messages
    );
    if (messageIndex === -1) {
      return;
    }
    messages[messageIndex].deliveryStatus = deliveryStatus;
    this.messages$.next(messages);
    this.messagesUtil.updateLocalStorageMessages(messages);
  }

  formatFileName(fileName: string): string {
    // Extracting the base name and extension
    const lastDotIndex = fileName.lastIndexOf('.');
    const baseName = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex + 1);

    // Taking the first 5 characters and creating the formatted file name
    const truncatedName = baseName.substring(0, 5);
    const formattedFileName = `${truncatedName}(...).${extension}`;

    return formattedFileName;
  }

  formatFileSize(sizeInBytes: number): string {
    const units = this.FILE_SIZE_UNITS;
    let power = Math.round(Math.log(sizeInBytes) / Math.log(1024));
    power = Math.min(power, units.length - 1);
    const size = sizeInBytes / Math.pow(1024, power); // size in new units
    const formattedSize = Math.round(size * 100) / 100; // keep up to 2 decimals
    const unit = units[power];
    return `${formattedSize} ${unit}`;
  }

  destroyService() {
    this.messages$.next([]);

    this.destroy$.next();
    this.destroy$.complete();
    this.messagesUtil.updateLocalStorageMessages([]);
  }
}
